import { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import Editable from './Editable';
import type { Translations, SeoData } from '../types';

interface ContactProps {
  t: Translations;
  seo: SeoData;
  isEditable: boolean;
  onUpdate: (key: string, val: string) => void;
}

const plainText = (value: string) => value.replace(/<[^>]*>/g, '').trim();

export default function Contact({ t, isEditable, onUpdate }: ContactProps) {
  const [formData, setFormData] = useState({ email: '', phone: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  
  const [loadCaptcha, setLoadCaptcha] = useState(false);
  
  const captchaRef = useRef<ReCAPTCHA>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'loading') return;
    if (!captchaToken) {
      alert('Proszę potwierdzić, że nie jesteś robotem.');
      return;
    }

    setStatus('loading');

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, captcha_token: captchaToken }),
      });

      if (!response.ok) {
        throw new Error('Błąd odpowiedzi serwera');
      }

      setStatus('success');
      setFormData({ email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Błąd wysyłania formularza:', error);
      setStatus('error');
    } finally {
      setCaptchaToken(null);
      captchaRef.current?.reset();
    }
  };

  const closeModal = () => setStatus('idle');

  return (
    <>
      <section id="kontakt" className="w-full bg-[#FAF8F5] py-24 lg:py-32 overflow-hidden relative">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-[#c69c6d]/15 rounded-full blur-3xl pointer-events-none"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">

              <div className="reveal-on-scroll">
                <span className="text-[#c69c6d] font-bold tracking-wider uppercase mb-3 flex items-center gap-2">
                  <i className="ri-message-3-line text-xl"></i>
                  <Editable isEditable={isEditable} value={t.contact_badge} onChange={(v) => onUpdate('contact_badge', v)} as="span" />
                </span>

                <Editable isEditable={isEditable} value={t.contact_heading} onChange={(v) => onUpdate('contact_heading', v)} as="h2" className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#125b60] leading-tight py-2" multiline />

                <Editable isEditable={isEditable} value={t.contact_desc} onChange={(v) => onUpdate('contact_desc', v)} as="div" className="mt-6 text-lg text-slate-600 leading-relaxed max-w-md py-2" multiline />

                <div className="mt-10 space-y-6">
                  {[
                    { link: t.contact_wa_link, label: 'contact_wa_label', val: 'contact_wa_number', icon: 'ri-whatsapp-line', color: 'text-[#25D366]', bg: 'bg-[#25D366]/10' },
                    { link: t.contact_phone_link, label: 'contact_phone_label', val: 'contact_phone_number', icon: 'ri-phone-line', color: 'text-[#125b60]', bg: 'bg-[#125b60]/10' },
                    { link: t.contact_email_link, label: 'contact_email_label', val: 'contact_email_address', icon: 'ri-mail-send-line', color: 'text-[#c69c6d]', bg: 'bg-[#c69c6d]/10' },
                  ].map((item, idx) => (
                    <a key={idx} href={item.link} onClick={(e) => isEditable && e.preventDefault()} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-5 p-5 bg-white rounded-2xl shadow-lg shadow-slate-200/50 hover:-translate-y-1 transition-all duration-300 border border-slate-100">
                      <div className={`w-14 h-14 ${item.bg} rounded-xl flex items-center justify-center ${item.color} transition-all duration-300 shrink-0`}>
                        <i className={`${item.icon} text-3xl`}></i>
                      </div>
                      <div className="w-full">
                        <Editable isEditable={isEditable} value={t[item.label]} onChange={(v) => onUpdate(item.label, v)} as="p" className="text-sm font-bold text-slate-400" />
                        <Editable isEditable={isEditable} value={t[item.val]} onChange={(v) => onUpdate(item.val, v)} as="p" className="text-lg font-bold text-slate-700" />
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              <div className="relative w-full max-w-xl mx-auto reveal-on-scroll">
                <div className="relative z-10 bg-white/80 backdrop-blur-md p-8 md:p-10 rounded-[2rem] shadow-2xl shadow-slate-200 border border-white">
                  {}
                  <form 
                    onSubmit={handleSubmit} 
                    className="space-y-5"
                    onMouseEnter={() => setLoadCaptcha(true)}
                    onTouchStart={() => setLoadCaptcha(true)}
                  >
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-black tracking-wider text-slate-500 uppercase mb-2 ml-1">
                        <Editable isEditable={isEditable} value={t.form_email_label} onChange={(v) => onUpdate('form_email_label', v)} as="span" />
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        aria-label={plainText(t.form_email_label || 'E-Mail')}
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        onFocus={() => setLoadCaptcha(true)}
                        className="w-full p-4 bg-slate-50/50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-4 focus:ring-[#c69c6d]/20 transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className="block text-xs font-black tracking-wider text-slate-500 uppercase mb-2 ml-1">
                        <Editable isEditable={isEditable} value={t.form_phone_label} onChange={(v) => onUpdate('form_phone_label', v)} as="span" />
                      </label>
                      <input
                        id="contact-phone"
                        name="phone"
                        type="tel"
                        required
                        autoComplete="tel"
                        aria-label={plainText(t.form_phone_label || 'Telefon')}
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        onFocus={() => setLoadCaptcha(true)}
                        className="w-full p-4 bg-slate-50/50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-4 focus:ring-[#c69c6d]/20 transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-black tracking-wider text-slate-500 uppercase mb-2 ml-1">
                        <Editable isEditable={isEditable} value={t.form_message_label} onChange={(v) => onUpdate('form_message_label', v)} as="span" />
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={4}
                        aria-label={plainText(t.form_message_label || 'Nachricht')}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        onFocus={() => setLoadCaptcha(true)}
                        className="w-full p-4 bg-slate-50/50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-4 focus:ring-[#c69c6d]/20 transition-all resize-none"
                      />
                    </div>

                    {}
                    {!isEditable && loadCaptcha && (
                      <div className="flex justify-center overflow-hidden py-2">
                        <ReCAPTCHA ref={captchaRef} sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || 'test-key'} onChange={(token) => setCaptchaToken(token)} />
                      </div>
                    )}

                    <div className="pt-2">
                      <button type="submit" disabled={status === 'loading' || isEditable} className="w-full py-4 bg-[#125b60] text-white font-bold rounded-xl transition-all disabled:opacity-70 flex items-center justify-center gap-3 hover:bg-[#104f53] disabled:cursor-not-allowed cursor-pointer">
                        {status === 'loading' ? (
                          <>
                            <i className="ri-loader-4-line text-xl animate-spin"></i>
                            <span>{t.form_sending || 'Wysyłanie...'}</span>
                          </>
                        ) : (
                          <>
                            <i className="ri-send-plane-fill text-xl"></i>
                            <Editable isEditable={isEditable} value={t.form_submit_btn} onChange={(v) => onUpdate('form_submit_btn', v)} as="span" />
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {(status === 'success' || status === 'error') && (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 transition-opacity duration-300" role="dialog" aria-modal="true" aria-labelledby="contact-modal-title">
          <div className="relative bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden flex flex-col items-center text-center p-8">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center text-4xl text-white mb-6 ${status === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
              <i className={status === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
            </div>
            <h2 id="contact-modal-title" className="text-2xl font-extrabold text-slate-800 mb-2">
              {status === 'success' ? t.form_success_title : t.form_error_title}
            </h2>
            <p className="text-slate-600 mb-8">
              {status === 'success' ? t.form_success_text : t.form_error_text}
            </p>
            <button onClick={closeModal} className="w-full py-3 bg-[#125b60] text-white font-bold rounded-lg hover:bg-[#104f53] transition-colors cursor-pointer">
              {t.form_modal_close_btn}
            </button>
          </div>
        </div>
      )}
    </>
  );
}